import { useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import type { MultiPolygon as GeoJsonMultiPolygon, Polygon as GeoJsonPolygon } from 'geojson';
import { useMemo } from 'react';
import { MdLayers } from 'react-icons/md';

import { getApiErrorMessage } from '~api/client';
import type { BlastLimitsResponse, HardnessDomainCollection, PatternDesignOptimisation } from '~api/services';
import { EmptyState, ErrorMessage, OverlayWidget } from '~components';
import { Button } from '~components/ui/button';
import { BASE_COLOURS } from '~config';
import {
  EnvironmentSitesLayer,
  HardnessZonesLayer,
  InfrastructureLayer,
  LayerControlPopover,
  LayerItem,
  LayerMask,
  OptimisationDrillHoles,
  Polygon,
  SensitiveSitesLayer,
  SvgCanvas,
  SvgGrid,
  SvgScaleLegend,
  UnsurveyedSitesLayer,
} from '~libs';
import type { BaseColour, ColourByParameter, OptimisationDrillHoleProperties } from '~types';
import { useCanvasLayers, useDrillHoleData } from '../hooks';
import { formatTooltip } from '../utils';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type OptimiserDrillHoleCanvasProps = {
  /** The optimisation data to visualize */
  optimisation: PatternDesignOptimisation;
  /** Optional blast polygon to outline the blast area */
  blastPolygon?: GeoJsonPolygon;
  /** Parameter to colour drill holes by */
  colourByParameter?: ColourByParameter;
  /** Colour for drill holes when a specific parameter is selected */
  colour?: BaseColour;
  /** Optional hardness domain zones to display */
  hardnessZones?: HardnessDomainCollection;
  /** Whether to show hardness zones (default: false) */
  showHardnessZones?: boolean;
  /** Optional blast limits information from where sensitive sites can be derived */
  blastLimits?: BlastLimitsResponse;
  /** Currently selected drill hole id (primitive string for memo stability) */
  selectedDrillHoleId: string | null;
  /** Callback when a drill hole is clicked */
  onDrillHoleClick: (hole: OptimisationDrillHoleProperties) => void;
};

type CanvasLayerControlsProps = Omit<
  ReturnType<typeof useCanvasLayers>,
  'hardnessDomains' | 'unsurveyedSites' | 'infrastructureSites' | 'environmentSites'
>;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

/**
 * Collect all geometries that should contribute to the canvas layout.
 * The order does not matter — they are only used for bounding box calculation.
 */
const collectCanvasGeometries = (
  blastPolygon?: GeoJsonPolygon,
  blastLimitsRadius?: GeoJsonPolygon,
): (GeoJsonPolygon | GeoJsonMultiPolygon)[] => {
  const geometries: (GeoJsonPolygon | GeoJsonMultiPolygon)[] = [];

  if (blastPolygon) geometries.push({ type: 'Polygon', coordinates: blastPolygon.coordinates });

  if (blastLimitsRadius) geometries.push(blastLimitsRadius);

  return geometries;
};

/* -------------------------------------------------------------------------- */
/*                               Sub-components                               */
/* -------------------------------------------------------------------------- */

/**
 * Layer controls panel rendered inside the canvas overlay.
 * Extracted to reduce the cyclomatic complexity of OptimiserDrillHoleCanvas.
 */
const CanvasLayerControls = (props: CanvasLayerControlsProps) => (
  <OverlayWidget>
    <LayerControlPopover
      className="divide-none"
      trigger={
        <Button
          icon
          variant="ghost"
          aria-label="Open layers panel"
          className={clsx(
            'min-h-10 rounded-md border border-border/20 bg-background p-2 text-primary shadow-md',
            'data-[state=open]:bg-primary data-[state=open]:text-primary-foreground',
            'data-[state=open]:hover:bg-primary data-[state=open]:dark:hover:text-primary',
          )}
        >
          <MdLayers className="size-5" />
        </Button>
      }
      title={
        <span className="flex items-center gap-2">
          <MdLayers className="size-4" />
          Map Layers
        </span>
      }
    >
      <LayerItem
        label="Hardness Zones"
        layerVisibility={props.hardnessZoneVisibility}
        isLoading={props.loadingHardnessDomains}
        // For this specific use case, we don't use the `errorMessage` to favour ErrorMessage below with a "retry" action.
        // We want to encourage users to retry loading hardness zones if it fails, and showing a tooltip with the error message on hover is not as effective for that.
        isError={props.isHardnessDomainsError}
      />
      {props.isHardnessDomainsError && (
        <ErrorMessage
          className="mb-4 rounded-md border-none p-4"
          collapsible={true}
          intent="error"
          title="Error loading hardness zones"
          message={String(getApiErrorMessage(props.hardnessDomainsError))}
          handleAction={() => props.refetchHardnessDomains()}
        />
      )}
      <LayerItem
        label="Unsurveyed Sites"
        layerVisibility={props.unsurveyedVisibility}
        isLoading={props.loadingUnsurveyedSites}
        isError={props.isUnsurveyedSitesError || (!props.loadingUnsurveyedSites && !props.haveUnsurveyedSites)}
        errorMessage={
          props.isUnsurveyedSitesError
            ? String(getApiErrorMessage(props.unsurveyedSitesError))
            : 'No unsurveyed sites have been found near the blast area.'
        }
      />
      <LayerItem
        label="Infrastructure Sites"
        layerVisibility={props.infrastructureVisibility}
        isLoading={props.loadingInfrastructureSites}
        isError={
          props.isInfrastructureSitesError || (!props.loadingInfrastructureSites && !props.haveInfrastructureSites)
        }
        errorMessage={
          props.isInfrastructureSitesError
            ? String(getApiErrorMessage(props.infrastructureSitesError))
            : 'No infrastructure sites have been found near the blast area.'
        }
      />
      <LayerItem
        label="Environment Sites"
        layerVisibility={props.environmentVisibility}
        isLoading={props.loadingEnvironmentSites}
        isError={props.isEnvironmentSitesError || (!props.loadingEnvironmentSites && !props.haveEnvironmentSites)}
        errorMessage={
          props.isEnvironmentSitesError
            ? String(getApiErrorMessage(props.environmentSitesError))
            : 'No environment sites have been found near the blast area.'
        }
      />
    </LayerControlPopover>
  </OverlayWidget>
);

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Canvas component to visualize drill holes and other spatial data related to a blast optimisation.
 * It includes multiple layers that can be toggled on and off, and a tooltip when hovering over drill holes or sensitive sites.
 *
 * @example
 * ```tsx
 * <OptimiserDrillHoleCanvas
 *   optimisation={optimisation}
 *   blastPolygon={blastPolygon}
 *   colourByParameter="depth"
 *   colour="orange"
 *   blastLimits={blastLimits}
 *   selectedDrillHoleId={selectedDrillHoleId}
 *   onDrillHoleClick={handleDrillHoleClick}
 * />
 * ```
 */
const OptimiserDrillHoleCanvas = ({
  optimisation,
  blastPolygon,
  colourByParameter = 'depth',
  colour = BASE_COLOURS.ORANGE,
  blastLimits,
  selectedDrillHoleId,
  onDrillHoleClick,
}: OptimiserDrillHoleCanvasProps) => {
  const { blastName } = useParams({ strict: false });
  const { hardnessDomains, unsurveyedSites, infrastructureSites, environmentSites, ...layerControls } = useCanvasLayers(
    { blastName },
  );

  // Extract drill holes from pattern design features
  const drillHoles = useDrillHoleData(optimisation);
  const blastLimitsRadius = blastLimits?.metadata?.inner_query_geojson as GeoJsonPolygon | undefined;

  // Memoize the filtered list so its reference stays stable between renders.
  // Without this, `.filter()` returns a new array every render, which would
  // invalidate the `geometries` memo below — triggering `computeCanvasLayout`
  // even when the underlying blast limits data hasn't changed.
  const sensitiveSites = useMemo(
    () => blastLimits?.sensitive_site_limits?.filter((site) => !site.ihmp_response?.is_cleared) ?? [],
    [blastLimits?.sensitive_site_limits],
  );
  const haveSensitiveSites = sensitiveSites.length > 0;

  const geometries = useMemo(
    () => collectCanvasGeometries(blastPolygon, blastLimitsRadius),
    [blastPolygon, blastLimitsRadius],
  );

  if (drillHoles.length === 0) {
    return (
      <EmptyState
        className="justify-start"
        title="No drill holes found"
        description="There are no drill holes available in this optimisation."
      />
    );
  }

  return (
    <section className="relative flex flex-1 items-center justify-center bg-background">
      <SvgCanvas
        description="Interactive map showing drill holes and hardness zones"
        boundedByGeometries={geometries}
        overlay={
          <>
            <CanvasLayerControls {...layerControls} />
            <SvgScaleLegend />
          </>
        }
      >
        {/* Layer 0: Adaptive grid */}
        <SvgGrid />

        {/* Layer 1: Hardness zones */}
        <LayerMask layerVisibility={layerControls.hardnessZoneVisibility}>
          {hardnessDomains?.domain && <HardnessZonesLayer domains={hardnessDomains.domain} benchLevel="middle" />}
        </LayerMask>

        {/* Layer 2: Blast polygon background */}
        {blastPolygon && (
          <Polygon geometry={blastPolygon} className="polygon-background" title="Blast polygon boundary" />
        )}

        {/* Layer 3: Blast limits radius */}
        {blastLimitsRadius && (
          <Polygon geometry={blastLimitsRadius} stroke="var(--warning)" strokeDasharray="4" fill="none" />
        )}

        {/* Layer 4: Sensitive sites */}
        {haveSensitiveSites && <SensitiveSitesLayer sensitiveSites={sensitiveSites} />}

        {/* Layer 5: Unsurveyed sites */}
        <LayerMask layerVisibility={layerControls.unsurveyedVisibility}>
          {unsurveyedSites && <UnsurveyedSitesLayer unsurveyedSites={unsurveyedSites} />}
        </LayerMask>

        {/* Layer 6: Infrastructure sites */}
        <LayerMask layerVisibility={layerControls.infrastructureVisibility}>
          {infrastructureSites && <InfrastructureLayer infrastructureSites={infrastructureSites} />}
        </LayerMask>

        {/* Layer 7: Environment sites */}
        <LayerMask layerVisibility={layerControls.environmentVisibility}>
          {environmentSites && <EnvironmentSitesLayer environmentSites={environmentSites} />}
        </LayerMask>

        {/* Layer 8: Drill holes */}
        <OptimisationDrillHoles
          drillHoles={drillHoles}
          colourByParameter={colourByParameter}
          colour={colour}
          selectedId={selectedDrillHoleId}
          onHoleSelect={onDrillHoleClick}
          titleFormatter={formatTooltip}
        />
      </SvgCanvas>
    </section>
  );
};

export { OptimiserDrillHoleCanvas };
